import { CUBE_AGGREGATE_INTERACTION, CUBE_AGGREGATE_INTERACTION_PRODUCT } from '@/api/hcp/interaction/types';
import { isNonEmptyString } from '@/api/queries/utils';
import { Channel, FilterValue } from '@/models';
import { FilterType } from '@/models/filter/FilterType';
import { getLocalOptionPaths } from '@/models/filter/utils/localOptionPathsStore';
import { useChannelFocusStore } from '@/pages/ChannelFocus/store/useChannelFocusStore';
import { CubeQuery, GetOptionsQueryFunction } from '@/types/cube';
import { Period } from '@/types/date';
import { FilterConfigDynamic } from '@/types/filter';
import { getDateRangeTimeDimension } from '@/utils/cube/helper';
import { Filter, LogicalAndFilter } from '@cubejs-client/core';
import { appendValueToEqualsFilters } from '../../utils/appendValueToEqualsFilters';
import expandHierarchicalPaths from '../../utils/expandHierarchicalPaths';
import { applyHierarchicalFilter, therapeuticIndicationDimensions } from '../../utils/hierarchicalFilter';
import { getDeepestHierarchyLevel } from '../../utils/hierarchyPath';
import mapHierarchicalOptionsForIndicationMode from '../../utils/mapHierarchicalOptionsForIndicationMode';
import { parseTaIndicationFilter } from '../../utils/parseTaIndicationFilter';

const interactionIndicationDimensions = therapeuticIndicationDimensions(CUBE_AGGREGATE_INTERACTION_PRODUCT);

export const buildInteractionIndicationFilter = (
  values: string[],
  dimensions: string[],
  additionalValue?: string,
  allPaths: string[] = getLocalOptionPaths(FilterType.INTERACTION_INDICATION),
): Filter | null => {
  if (allPaths.length) {
    const filter = parseTaIndicationFilter(values, allPaths, dimensions);
    return filter ? appendValueToEqualsFilters(filter, additionalValue) : null;
  }

  const fallbackFilter: LogicalAndFilter = { and: [] };
  applyHierarchicalFilter(fallbackFilter, values, dimensions, Boolean(additionalValue), additionalValue);
  return fallbackFilter.and.length ? fallbackFilter : null;
};

const TA_INDICATION_OPTIONS: GetOptionsQueryFunction = ({ dateRange }: { dateRange: Period }) => {
  const timeDimensions = getDateRangeTimeDimension({
    dateRange,
    dimension: `${CUBE_AGGREGATE_INTERACTION}.interaction_date`,
  });

  const store = useChannelFocusStore.getState();
  const channelValue = store?.channel;

  const baseFilters: LogicalAndFilter = {
    and: [
      { member: `${CUBE_AGGREGATE_INTERACTION}.hcp_bk`, operator: 'set' },
      { member: `${CUBE_AGGREGATE_INTERACTION_PRODUCT}.global_therapeutic_area_name`, operator: 'set' },
    ],
  };

  const channelFilters: LogicalAndFilter = {
    and: [],
  };

  if (channelValue) {
    switch (channelValue) {
      case Channel.EMAIL:
        channelFilters.and.push({
          member: `${CUBE_AGGREGATE_INTERACTION}.channel`,
          operator: 'equals',
          values: ['Email'],
        });
        break;
      case Channel.WEBSITE:
        channelFilters.and.push({
          member: `${CUBE_AGGREGATE_INTERACTION}.is_website_visit`,
          operator: 'equals',
          values: ['true'],
        });
        break;
      case Channel.VISITS_1_TO_1:
        channelFilters.and.push({
          member: `${CUBE_AGGREGATE_INTERACTION}.is_1to1visit`,
          operator: 'set',
        });
        break;
    }
  }

  const query: CubeQuery = {
    dimensions: interactionIndicationDimensions,
    limit: 1000,
    filters: [baseFilters, channelFilters],
    timeDimensions: [timeDimensions],
  };
  return query;
};

const interactionIndicationFilter: FilterConfigDynamic = {
  label: 'main.channelFocus.hcpCoverage.filters.taIndication.label',
  placeholder: 'main.channelFocus.hcpCoverage.filters.taIndication.placeholder',
  type: 'treeselect',
  searchable: true,
  clearable: true,
  selectionMode: 'indication',
  defaultValue: [],
  sourceCube: CUBE_AGGREGATE_INTERACTION_PRODUCT,
  getOptionsQuery: TA_INDICATION_OPTIONS,
  mapResult: row => mapHierarchicalOptionsForIndicationMode(row, interactionIndicationDimensions),
  processOptions: expandHierarchicalPaths,
  recordsOptionPaths: true,
  format: ({ value }) => {
    if (!isNonEmptyString(value)) return '';
    return getDeepestHierarchyLevel(value);
  },
  applyFilter: (values: FilterValue, context) => {
    if (!Array.isArray(values) || !values.every(valueItem => typeof valueItem === 'string')) return undefined;

    return (
      buildInteractionIndicationFilter(values, interactionIndicationDimensions, undefined, context?.optionPaths) ??
      undefined
    );
  },
};

export default interactionIndicationFilter;
